import React from 'react'
import classnames from 'classnames/bind'
import {
  URN,
  CognitionEvaluationGroup,
  CognitionAuthenticationStatus,
} from '@adc/sdk/types'
import * as graph from '@adc/sdk'
import {
  TextCell,
  DateCell,
  InfoPillCell,
  COLUMN_SORT_SETTING,
  COLUMN_TYPES,
  // @ts-ignore next
} from '@adc/datatable'
// @ts-ignore next
import { CopyButton, Icon, coreConstants } from '@adc/ui-morpheus'
import EditCell from './editCell'
import { statusColorMapper } from './utils'
import styles from './styles.scss'

const cx = classnames.bind(styles)

const { ColorNames } = coreConstants

const NET_SCORE_COLUMN_WIDTH = 110

interface ColProps {
  isResizable: boolean
  isReorderable: boolean
  isVisible: boolean
  searchable: boolean
  align: string
  flexGrow: number
  sort: string
  width: number
  fixedRight?: boolean
  initialSortDirection?: string
}

interface Column {
  id: string
  title: string
  Cell: React.ComponentType<unknown>
  apiRef: string
  sortField?: string
  accessor: (row: unknown) => unknown
  colProps: ColProps
  field?: string
  type?: string
}

interface SortingFilter {
  columnKey: string
  direction: string
}

interface ActionHandlers {
  onViewClick: (value: unknown, rowIndex: number) => void
}

interface GenerateColumnsParams {
  sortingFilter: SortingFilter
  actionHandlers: ActionHandlers
}

export type { GenerateColumnsParams, Column, SortingFilter, ActionHandlers }

export const currentStandardColProps: Omit<ColProps, 'width'> = {
  isResizable: true,
  isReorderable: true,
  isVisible: true,
  searchable: false,
  align: 'left',
  flexGrow: 0,
  sort: COLUMN_SORT_SETTING.NOT_SORTED, // eslint-disable-line
}

interface CellProps {
  value: string | number
  rowIndex: number
  [key: string]: string | number
}

/**
 * URN cell with copy button
 */
interface UrnCellProps extends CellProps {
  value: URN
}

const UrnWithCopy: React.FC<UrnCellProps> = ({
  value,
  ...rest
}: {
  value: URN
}): React.ReactElement => {
  const uuid = value?.split('adc:evaluation_group:')[1]
  const prettyValue = uuid?.split('-')[0]

  return (
    <div>
      <TextCell
        value={
          <>
            <code>{prettyValue || value}</code>
            <CopyButton
              text={value}
              size='small'
              elmId='urn-copy'
              showSuccessFeedback={false}
            />
          </>
        }
        dataValue={value}
        {...rest}
        classes={cx('urn-cell')}
      />
    </div>
  )
}

/**
 * Cell displaying authentication status with icon
 */
interface AuthStatusCellProps extends CellProps {
  value: CognitionAuthenticationStatus
}

const AUTH_STATUS_CONFIG: Record<
  CognitionAuthenticationStatus,
  { label: string; icon: string; color: string }
> = {
  [graph.enums.CognitionAuthenticationStatus.LoggedOut]: {
    label: 'Logged Out',
    icon: 'person-circle-question',
    color: ColorNames.Gray,
  },
  [graph.enums.CognitionAuthenticationStatus.Promoted]: {
    label: 'Promoted',
    icon: 'person-rays',
    color: ColorNames.InfoActiveMedium,
  },
  [graph.enums.CognitionAuthenticationStatus.LoggedIn]: {
    label: 'Logged In',
    icon: 'person-circle-check',
    color: ColorNames.SuccessActiveMedium,
  },
}

const AuthStatusCell: React.FC<AuthStatusCellProps> = ({
  value,
  ...rest
}: {
  value: CognitionAuthenticationStatus
}) => {
  const config = value ? AUTH_STATUS_CONFIG[value] : null
  return (
    <TextCell
      value={
        config ? (
          <div className={cx('cell-number-with-icon')}>
            <Icon
              iconStyle='solid'
              name={config.icon}
              color={config.color}
              size='large'
              elmId='auth_status_icon'
            />
          </div>
        ) : null
      }
      dataValue={config?.label || ''}
      {...rest}
    />
  )
}

/**
 * Cell displaying ranking with thumbs up/down icon
 */
interface RankingCellProps extends CellProps {
  value: number
}

const RankingCell: React.FC<RankingCellProps> = ({
  value,
  ...rest
}: {
  value: number
}) => {
  return (
    <TextCell
      value={
        value && (
          <div className={cx('cell-number-with-icon')}>
            {Math.abs(value) > 1 ? (
              <strong
                data-elm-id='ranking_value'
                className={
                  value > 0 ? cx('thumbs-up-text') : cx('thumbs-down-text')
                }
              >
                {value}
              </strong>
            ) : (
              ''
            )}
            <Icon
              iconStyle='solid'
              name={value > 0 ? 'thumbs-up' : 'thumbs-down'}
              color={value > 0 ? 'successActiveMedium' : 'errorActiveMedium'}
              size='large'
              elmId='external_user_rating_icon'
              style={{ marginTop: value > 0 ? '-7px' : '0px' }}
            />
          </div>
        )
      }
      dataValue={value || ''}
      {...rest}
    />
  )
}

/**
 * Cell displaying comment count with message icon
 */
interface CommentCountCellProps extends CellProps {
  value: number
}
const CommentCountCell: React.FC<CommentCountCellProps> = ({
  value,
  ...rest
}: {
  value: number
}) => {
  return (
    <TextCell
      value={
        value && (
          <div className={cx('cell-number-with-icon')}>
            {Math.abs(value) > 1 ? (
              <strong
                className={cx('comment-text')}
                data-elm-id='comment_count_value'
              >
                {value}
              </strong>
            ) : (
              ''
            )}
            <Icon
              iconStyle='solid'
              name='message'
              color='infoActiveMedium'
              size='large'
              elmId='external_comment_count_icon'
            />
          </div>
        )
      }
      dataValue={value || ''}
      {...rest}
    />
  )
}

/**
 * Generate columns configuration for the evaluations table
 * @param params - Configuration parameters
 * @returns Array of column definitions
 */
export const generateColumns = ({
  sortingFilter,
  actionHandlers,
}: GenerateColumnsParams): Column[] => {
  // @ts-ignore next
  return [
    {
      id: 'urn',
      title: 'URN',
      Cell: ({ value, ...rest }: CellProps) => {
        return <UrnWithCopy value={value as URN} {...rest} />
      },
      apiRef: 'urn',
      sortField: 'urn.keyword',
      accessor: (row: CognitionEvaluationGroup) => row.urn,
      colProps: {
        ...currentStandardColProps,
        width: 100,
        searchable: true,
        sort: COLUMN_SORT_SETTING.NOT_SORTABLE, // eslint-disable-line
      },
    },
    {
      id: 'evaluations.method',
      title: 'Method(s)',
      Cell: (props: CellProps) => (
        <TextCell
          {...props}
          classes={cx('no-pad-cell')}
          style={{ fontSize: 12 }}
        />
      ),
      apiRef: 'evaluations.method',
      accessor: (row: CognitionEvaluationGroup) =>
        row.evaluations?.map(evaluation => evaluation.method).join(', '),
      colProps: {
        ...currentStandardColProps,
        width: 250,
        sort: COLUMN_SORT_SETTING.NOT_SORTABLE, // eslint-disable-line
        searchable: false,
        isVisible: false,
      },
    },
    {
      id: 'evaluations.method_version',
      title: 'Method-Version(s)',
      Cell: (props: CellProps) => (
        <TextCell
          {...props}
          classes={cx('no-pad-cell')}
          style={{ fontSize: 12 }}
        />
      ),
      apiRef: 'evaluations.method_version',
      accessor: (row: CognitionEvaluationGroup) =>
        row.evaluations
          ?.map(evaluation => evaluation.method_version)
          .join(', '),
      colProps: {
        ...currentStandardColProps,
        width: 250,
        sort: COLUMN_SORT_SETTING.NOT_SORTABLE, // eslint-disable-line
        searchable: true,
      },
    },
    {
      id: 'status',
      title: 'Status',
      Cell: (props: CellProps) =>
        props?.value && (
          <InfoPillCell
            {...props}
            size='x-small'
            colorMapper={statusColorMapper}
          />
        ),
      apiRef: 'status',
      accessor: (row: CognitionEvaluationGroup) => row.status,
      colProps: {
        ...currentStandardColProps,
        width: 130,
      },
    },
    {
      id: 'authentication_status',
      title: 'Auth Status',
      Cell: ({ value, ...rest }: CellProps) => {
        return (
          <AuthStatusCell
            value={value as CognitionAuthenticationStatus}
            {...rest}
          />
        )
      },
      apiRef: 'authentication_status',
      accessor: (row: CognitionEvaluationGroup) =>
        row.authentication_status || '',
      colProps: {
        ...currentStandardColProps,
        align: 'center',
        isVisible: true,
        width: 100,
      },
    },
    {
      id: 'created_date',
      title: 'Created Date',
      Cell: (props: CellProps) => (
        <DateCell
          {...props}
          classes={cx('no-pad-cell')}
          format='MMM DD, YYYY - hh:mm A'
        />
      ),
      apiRef: 'created_date',
      accessor: (row: CognitionEvaluationGroup) => row.created_date,
      colProps: {
        ...currentStandardColProps,
        width: 190,
      },
    },
    {
      id: 'created_by_user.email',
      title: 'Created By',
      Cell: (props: CellProps) => (
        <TextCell classes={cx('no-pad-cell')} {...props} />
      ),
      apiRef: 'created_by_user.email',
      accessor: (row: CognitionEvaluationGroup) => row.created_by_user?.email,
      colProps: {
        ...currentStandardColProps,
        sort: COLUMN_SORT_SETTING.NOT_SORTABLE, // eslint-disable-line
        width: 200,
        isVisible: false,
      },
    },
    {
      id: 'owner_urn',
      title: 'Owner',
      Cell: (props: CellProps) => (
        <TextCell classes={cx('no-pad-cell')} {...props} />
      ),
      apiRef: 'owner_urn', // not used, just avoiding graph error
      accessor: (row: CognitionEvaluationGroup) => {
        const owner = row.owner
        if (!owner) return ''
        // Assume party is the owner as right now only user can own eval groups.
        if ('user' in owner) return owner.user?.email
        return ''
      },
      colProps: {
        ...currentStandardColProps,
        sort: COLUMN_SORT_SETTING.NOT_SORTABLE, // eslint-disable-line
        width: 200,
      },
    },
    {
      id: 'updated_date',
      title: 'Updated Date',
      Cell: (props: CellProps) => (
        <DateCell
          {...props}
          classes={cx('no-pad-cell')}
          format='MMM DD, YYYY - hh:mm A'
        />
      ),
      apiRef: 'updated_date',
      accessor: (row: CognitionEvaluationGroup) => row.updated_date,
      colProps: {
        ...currentStandardColProps,
        isVisible: false,
        width: 200,
      },
    },
    {
      id: 'updated_by_user.email',
      title: 'Updated By',
      Cell: (props: CellProps) => (
        <TextCell classes={cx('no-pad-cell')} {...props} />
      ),
      apiRef: 'updated_by_user.email',
      accessor: (row: CognitionEvaluationGroup) => row.updated_by_user?.email,
      colProps: {
        ...currentStandardColProps,
        sort: COLUMN_SORT_SETTING.NOT_SORTABLE, // eslint-disable-line
        isVisible: false,
        width: 200,
      },
    },
    {
      id: 'assignee_urn',
      title: 'Assignee',
      Cell: (props: CellProps) => (
        <TextCell classes={cx('no-pad-cell')} {...props} />
      ),
      apiRef: 'assignee_urn', // not used directly
      accessor: (row: CognitionEvaluationGroup) =>
        // @ts-ignore CognitionEvaluationGroup type does not yet have assignee field
        row.assignee?.web_communications[0]?.identifier, // eslint-disable-line
      colProps: {
        ...currentStandardColProps,
        sort: COLUMN_SORT_SETTING.NOT_SORTABLE, // eslint-disable-line
        isVisible: false,
        width: 200,
      },
    },
    {
      id: 'last_scored_at',
      title: 'Last Scored On',
      Cell: (props: CellProps) => (
        <DateCell
          {...props}
          classes={cx('no-pad-cell')}
          format='MMM DD, YYYY - hh:mm A'
        />
      ),
      apiRef: 'last_scored_at',
      accessor: (row: CognitionEvaluationGroup) => row.last_scored_at,
      colProps: {
        ...currentStandardColProps,
        isVisible: true,
        width: 190,
      },
    },
    {
      id: 'default_person_net_ranking',
      title: 'End User Ranking',
      Cell: ({ value, ...rest }: CellProps) => {
        return <RankingCell value={value as number} {...rest} />
      },
      apiRef: 'default_person_net_ranking',
      accessor: (row: CognitionEvaluationGroup) =>
        row.default_person_net_ranking || '',
      colProps: {
        ...currentStandardColProps,
        align: 'center',
        isVisible: true,
        width: NET_SCORE_COLUMN_WIDTH,
      },
    },
    {
      id: 'trusted_person_net_ranking',
      title: 'Trusted User Net Ranking',
      Cell: ({ value, ...rest }: CellProps) => {
        return <RankingCell value={value as number} {...rest} />
      },
      apiRef: 'trusted_person_net_ranking',
      accessor: (row: CognitionEvaluationGroup) =>
        row.trusted_person_net_ranking || '',
      colProps: {
        ...currentStandardColProps,
        align: 'center',
        isVisible: true,
        width: NET_SCORE_COLUMN_WIDTH,
      },
    },
    {
      id: 'system_net_ranking',
      title: 'Judge Net Ranking',
      Cell: ({ value, ...rest }: CellProps) => {
        return <RankingCell value={value as number} {...rest} />
      },
      apiRef: 'system_net_ranking',
      accessor: (row: CognitionEvaluationGroup) => row.system_net_ranking || '',
      colProps: {
        ...currentStandardColProps,
        align: 'center',
        isVisible: true,
        width: NET_SCORE_COLUMN_WIDTH,
      },
    },
    {
      id: 'net_ranking',
      title: 'Overall Net Ranking',
      Cell: ({ value, ...rest }: CellProps) => {
        return <RankingCell value={value as number} {...rest} />
      },
      apiRef: 'net_ranking',
      accessor: (row: CognitionEvaluationGroup) => row.net_ranking || '',
      colProps: {
        ...currentStandardColProps,
        align: 'center',
        isVisible: true,
        width: NET_SCORE_COLUMN_WIDTH,
      },
    },
    {
      id: 'comment_count',
      title: 'Comment Count',
      Cell: ({ value, ...rest }: CellProps) => {
        return <CommentCountCell value={value as number} {...rest} />
      },
      apiRef: 'comment_count',
      accessor: (row: CognitionEvaluationGroup) => row.comment_count || '',
      colProps: {
        ...currentStandardColProps,
        align: 'center',
        isVisible: true,
        width: 100,
      },
    },
    {
      id: 'data_set.description',
      title: 'Data Set',
      sortField: 'data_set.description.keyword',
      Cell: (props: CellProps) => {
        return (
          <TextCell
            {...props}
            classes={cx('no-pad-cell')}
            style={{ fontSize: 12 }}
          />
        )
      },
      apiRef: 'data_set.description',
      accessor: (row: CognitionEvaluationGroup) =>
        row?.data_set?.description || '',
      colProps: {
        ...currentStandardColProps,
        isVisible: true,
        width: 250,
        searchable: true,
      },
    },
    {
      id: 'data_set.tags',
      title: 'Tags',
      Cell: ({ value, ...rest }: CellProps) => {
        return <TextCell value={value} {...rest} />
      },
      apiRef: 'data_set.tags',
      accessor: (row: CognitionEvaluationGroup) => row?.data_set?.tags || '',
      colProps: {
        ...currentStandardColProps,
        isVisible: true,
        width: 150,
        searchable: true,
      },
    },
    {
      id: '__actions__',
      type: COLUMN_TYPES.ACTION, // eslint-disable-line
      accessor: (row: CognitionEvaluationGroup) => row,
      Cell: (props: CellProps) => (
        <EditCell
          onClick={(value, rowIndex: number) =>
            actionHandlers.onViewClick(value, rowIndex)
          }
          {...props}
        />
      ),
      colProps: {
        ...currentStandardColProps,
        width: 40,
        fixedRight: true,
        align: 'center',
        isResizable: false,
        isReorderable: false,
        isVisible: false,
      },
    },
  ].map(column => {
    if (sortingFilter.columnKey === column.id) {
      column.colProps.sort = COLUMN_SORT_SETTING.SORTED // eslint-disable-line
      column.colProps.initialSortDirection = sortingFilter.direction
    }
    // @ts-ignore next
    column.field = column.apiRef
    column.sortField = column.sortField || column.apiRef
    return column
  })
}

type PartialColumn = {
  title: Column['title']
  field: Column['field']
  type?: Column['type']
  options?: { value: unknown; text: string }[]
  colProps?: Partial<ColProps>
}

export const columnsForDownload: PartialColumn[] = [
  {
    title: 'Method',
    field: 'evaluations.method',
    colProps: { isVisible: true },
  },
  {
    title: 'Is Winner',
    field: 'evaluations.is_winner',
    colProps: { isVisible: true },
  },
  {
    title: 'Score',
    field: 'evaluations.rubrics.score',
    colProps: { isVisible: true },
    type: 'VALUE_TEXT',
    options: [
      {
        value: null,
        text: '',
      },
      {
        value: true,
        text: '1',
      },
      {
        value: false,
        text: '-1',
      },
    ],
  },
  {
    title: 'Eval User Email',
    field: 'evaluations.rubrics.evaluator_user.email',
    colProps: { isVisible: true },
  },
  {
    title: 'EvalUser Name',
    field: 'evaluations.rubrics.evaluator_user.display_name',
    colProps: { isVisible: true },
  },
  {
    title: 'Notes',
    field: 'evaluations.rubrics.notes',
    colProps: { isVisible: true },
  },
]
